import { api, unwrapList } from '../../../shared/lib/axios';

/**
 * API client do módulo Propostas Comerciais (US-COM-006, change
 * `gestao-propostas-comerciais`).
 *
 * **Cada VERSÃO é a unidade de negócio.** `id` da versão é o que outros
 * módulos (Contratos, OS, Financeiro) devem persistir — o agrupador
 * (`proposalId`) muda com o tempo, a versão não (RN-COM-006/026).
 *
 * Todo `numeric` chega como string do Postgres para preservar precisão
 * — formatar no cliente via helpers de `utils/format.ts`.
 */

// ---- Tipos base -----------------------------------------------------

export type StatusProposta =
  | 'em_elaboracao'
  | 'em_revisao'
  | 'em_aprovacao'
  | 'aguardando_complementacao'
  | 'aprovada_internamente'
  | 'enviada_ao_cliente'
  | 'em_negociacao'
  | 'aceita'
  | 'rejeitada'
  | 'cancelada'
  | 'expirada'
  | 'convertida_em_contrato'
  | 'substituida';

export type OrigemProposta =
  | 'oportunidade'
  | 'renovacao'
  | 'upsell'
  | 'cross_sell'
  | 'solicitacao_direta'
  | 'licitacao'
  | 'atualizacao_contrato';

export type MeioEnvioProposta = 'email' | 'portal' | 'whatsapp' | 'entrega_fisica';

export type DecisaoAprovacaoProposta = 'pendente' | 'aprovada' | 'rejeitada' | 'devolvida';

export type TipoEventoNegociacao =
  | 'reuniao'
  | 'ligacao'
  | 'email'
  | 'contraproposta'
  | 'solicitacao_cliente'
  | 'alteracao';

export type StatusAssinaturaProposta =
  | 'requested'
  | 'in_progress'
  | 'completed'
  | 'failed'
  | 'cancelled';

export type ProviderAssinaturaProposta = 'd4sign' | 'clicksign' | 'docusign';

// ---- Entidades ------------------------------------------------------

export interface Proposta {
  id: string;
  codigo: string;
  clientId: string;
  grupoEconomicoId?: string;
  responsavelComercialUserId: string;
  origem: OrigemProposta;
  sourceOpportunityId?: string;
  sourceOpportunitySnapshotAt?: string;
  currentVersionId?: string;
  dataEmissao: string;
  convertedFromProposalVersionId?: string;
  convertedAt?: string;
  cancelledAt?: string;
  cancelledBy?: string;
  cancelReason?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PropostaVersao {
  id: string;
  proposalId: string;
  versionNumber: string;
  status: StatusProposta;
  commercialBaseVersionId: string;
  commercialBaseSnapshot?: Record<string, unknown>;
  escopo?: string;
  observacoes?: string;
  dataValidade: string;
  previsaoFechamentoAt?: string;
  valorTotal: string;
  descontoPercentual: string;
  descontoValorAbsoluto: string;
  acrescimoValorAbsoluto: string;
  margemEstimadaValor?: string;
  margemEstimadaPercentual?: string;
  motivoAlteracao?: string;
  changeDiff?: Record<string, unknown>;
  createdBy: string;
  supersededByVersionId?: string;
  dispatchedAt?: string;
  acceptedAt?: string;
  rejectedAt?: string;
  expiredAt?: string;
  convertedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PropostaComVersoes extends Proposta {
  versions: PropostaVersao[];
}

export interface PropostaListItem extends Proposta {
  currentVersion?: PropostaVersao | null;
}

export interface PropostaItem {
  id: string;
  proposalVersionId: string;
  servicoSlug: string;
  produto?: string;
  perfilProfissionalSlug?: string;
  unidadeMedida: string;
  quantidade: string;
  valorUnitario: string;
  descontoPercentual: string;
  descontoValor: string;
  acrescimoPercentual: string;
  acrescimoValor: string;
  valorTotalItem: string;
  custoUnitario?: string;
  observacoes?: string;
  isOverride: boolean;
  overrideJustification?: string;
}

export interface PropostaAprovacao {
  id: string;
  proposalVersionId: string;
  workflowId: string;
  workflowStepId: string;
  stepIndex: number;
  roleSlug: string;
  featureRequired: string;
  status: DecisaoAprovacaoProposta;
  decidedAt?: string;
  decidedByUserId?: string;
  comment?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PropostaAnexo {
  id: string;
  proposalId: string;
  filename: string;
  mimeType: string;
  sizeBytes: string;
  storageKey: string;
  uploadedBy: string;
  createdAt: string;
}

export interface PropostaDispatchRecipient {
  contactId: string;
  displayName: string;
  email?: string;
  telefone?: string;
  isPrimary?: boolean;
}

export interface PropostaDispatch {
  id: string;
  proposalVersionId: string;
  dispatchedAt: string;
  dispatchedBy: string;
  medium: MeioEnvioProposta;
  recipients: PropostaDispatchRecipient[];
  documentHash: string;
  documentKey: string;
  documentGeneratedAt: string;
  notes?: string;
  createdAt: string;
}

export interface PropostaAceite {
  id: string;
  proposalVersionId: string;
  acceptedAt: string;
  acceptedByContactId: string;
  evidenceAttachmentId?: string;
  evidenceHashFromSignature?: string;
  signatureExternalReference?: string;
  recordedBy: string;
  observacoes?: string;
  createdAt: string;
}

export interface PropostaRejeicao {
  id: string;
  proposalVersionId: string;
  rejectedAt: string;
  recordedBy: string;
  motivo: string;
  justificativa?: string;
  concorrenteVencedor?: string;
  createdAt: string;
}

export interface PropostaEventoNegociacao {
  id: string;
  proposalVersionId: string;
  tipo: TipoEventoNegociacao;
  occurredAt: string;
  recordedBy: string;
  resumo: string;
  attachments?: unknown;
  generatedVersionId?: string;
  createdAt: string;
}

export interface PropostaAssinatura {
  id: string;
  proposalVersionId: string;
  externalReference: string;
  status: StatusAssinaturaProposta;
  provider: ProviderAssinaturaProposta;
  signers: unknown;
  requestedAt: string;
  requestedBy: string;
  signedAt?: string;
  evidenceHash?: string;
  signerIp?: string;
  signerGeo?: string;
  failureReason?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PropostaHistoricoItem {
  id: string;
  proposalId: string;
  proposalVersionId?: string;
  action: string;
  entity: string;
  entityId?: string;
  fieldName?: string;
  valueBefore?: unknown;
  valueAfter?: unknown;
  justificativa?: string;
  userId: string;
  createdAt: string;
}

export interface EligibleContactSummary {
  id: string;
  clientId: string;
  nome: string;
  email?: string | null;
  celular?: string | null;
  telefone?: string | null;
  isPrincipal: boolean;
}

export interface RecipientForVersion {
  recipientId: string;
  clientContactId: string;
  isPrimary: boolean;
  currentStatus: string | null;
  nome: string | null;
  email: string | null;
  preservadoHistorico: boolean;
}

export interface PropostaVersionDiff {
  from: { id: string; versionNumber: string };
  to: { id: string; versionNumber: string };
  changedFields: Array<{ field: string; before: unknown; after: unknown }>;
  itemsAdded: number;
  itemsRemoved: number;
  itemsChanged: number;
}

// ---- Payloads -------------------------------------------------------

export interface CreateProposalPayload {
  clientId: string;
  commercialBaseId: string;
  responsavelComercialUserId: string;
  dataValidade: string;
  previsaoFechamentoAt?: string;
  escopo?: string;
  observacoes?: string;
  origem?: OrigemProposta;
  sourceOpportunityId?: string;
}

export interface CreateProposalFromOpportunityPayload {
  commercialBaseId: string;
  dataValidade: string;
  responsavelComercialUserIdOverride?: string;
  escopoOverride?: string;
  observacoesOverride?: string;
}

export interface UpdateProposalPayload {
  responsavelComercialUserId?: string;
  dataValidade?: string;
  previsaoFechamentoAt?: string;
  escopo?: string;
  observacoes?: string;
}

export interface AddProposalItemPayload {
  servicoSlug: string;
  produto?: string;
  perfilProfissionalSlug?: string;
  unidadeMedida: string;
  quantidade: number;
  valorUnitario: number;
  descontoPercentual?: number;
  descontoValor?: number;
  acrescimoPercentual?: number;
  acrescimoValor?: number;
  custoUnitario?: number;
  observacoes?: string;
  isOverride?: boolean;
  overrideJustification?: string;
}

export type UpdateProposalItemPayload = Partial<AddProposalItemPayload>;

export interface DispatchProposalPayload {
  recipients: Array<{ contactId: string; isPrimary?: boolean }>;
  medium: MeioEnvioProposta;
  notes?: string;
}

export interface RecordNegotiationPayload {
  tipo: TipoEventoNegociacao;
  resumo: string;
  occurredAt?: string;
  attachments?: Record<string, unknown>;
  reasonForNewVersion?: string;
}

export interface RecordAcceptancePayload {
  acceptedByContactId: string;
  evidenceAttachmentId: string;
  observacoes?: string;
  acceptedAt?: string;
}

export interface RecordRejectionPayload {
  motivo: string;
  justificativa?: string;
  concorrenteVencedor?: string;
  rejectedAt?: string;
}

export interface ConvertToContractPayload {
  contractType: string;
  effectiveStartDate: string;
  effectiveEndDate?: string;
  paymentTerms?: Record<string, unknown>;
}

export interface DecideApprovalPayload {
  decision: Exclude<DecisaoAprovacaoProposta, 'pendente'>;
  comment?: string;
}

export interface RequestSignaturePayload {
  provider: ProviderAssinaturaProposta;
  callbackUrl: string;
  signers: Array<{
    contactId: string;
    displayName: string;
    email: string;
    order?: number;
  }>;
}

// ---- Listagem -------------------------------------------------------

export interface ListPropostasParams {
  clientId?: string;
  responsavelComercialUserId?: string;
  status?: StatusProposta;
  origem?: OrigemProposta;
  sourceOpportunityId?: string;
  limit?: number;
  offset?: number;
}

export interface ListPropostasResponse {
  data: PropostaListItem[];
  total: number;
  limit: number;
  offset: number;
}

// ---- Query keys -----------------------------------------------------

export const PROPOSAL_KEYS = {
  all: ['propostas'] as const,
  list: (params?: ListPropostasParams) => ['propostas', 'list', params ?? null] as const,
  detail: (id: string) => ['propostas', 'detail', id] as const,
  items: (versionId: string) => ['propostas', 'items', versionId] as const,
  versions: (id: string) => ['propostas', 'versions', id] as const,
  diff: (id: string, vA: string, vB: string) => ['propostas', 'diff', id, vA, vB] as const,
  approvals: (versionId: string) => ['propostas', 'approvals', versionId] as const,
  attachments: (id: string, versionId?: string) =>
    ['propostas', 'attachments', id, versionId ?? null] as const,
  recipients: (versionId: string) => ['propostas', 'recipients', versionId] as const,
  eligibleContacts: (id: string) => ['propostas', 'eligible-contacts', id] as const,
  negotiation: (versionId: string) => ['propostas', 'negotiation', versionId] as const,
};

// ---- Helper ---------------------------------------------------------

function toQueryString(params?: Record<string, unknown>): string {
  if (!params) return '';
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `?${s}` : '';
}

// ---- Leitura --------------------------------------------------------

export async function listProposals(
  params?: ListPropostasParams,
): Promise<ListPropostasResponse> {
  const { data } = await api.get<ListPropostasResponse>(
    `/proposals${toQueryString(params as Record<string, unknown>)}`,
  );
  return data;
}

export async function getProposal(id: string): Promise<PropostaComVersoes> {
  const { data } = await api.get<PropostaComVersoes>(`/proposals/${id}`);
  return data;
}

export async function listItems(
  proposalId: string,
  versionId: string,
): Promise<PropostaItem[]> {
  const { data } = await api.get<PropostaItem[] | { data: PropostaItem[] }>(
    `/proposals/${proposalId}/versions/${versionId}/items`,
  );
  return unwrapList<PropostaItem>(data);
}

export async function listApprovals(versionId: string): Promise<PropostaAprovacao[]> {
  const { data } = await api.get<PropostaAprovacao[] | { data: PropostaAprovacao[] }>(
    `/proposal-approvals/version/${versionId}`,
  );
  return unwrapList<PropostaAprovacao>(data);
}

export async function listAttachments(
  proposalId: string,
  versionId?: string,
): Promise<PropostaAnexo[]> {
  const suffix = versionId ? `?versionId=${versionId}` : '';
  const { data } = await api.get<PropostaAnexo[] | { data: PropostaAnexo[] }>(
    `/proposals/${proposalId}/attachments${suffix}`,
  );
  return unwrapList<PropostaAnexo>(data);
}

export async function listEligibleContacts(
  proposalId: string,
): Promise<EligibleContactSummary[]> {
  const { data } = await api.get<
    EligibleContactSummary[] | { data: EligibleContactSummary[] }
  >(`/proposals/${proposalId}/eligible-contacts`);
  return unwrapList<EligibleContactSummary>(data);
}

export async function listRecipientsForVersion(
  proposalId: string,
  versionId: string,
): Promise<RecipientForVersion[]> {
  const { data } = await api.get<
    RecipientForVersion[] | { data: RecipientForVersion[] }
  >(`/proposals/${proposalId}/versions/${versionId}/recipients`);
  return unwrapList<RecipientForVersion>(data);
}

export async function getVersionDiff(
  proposalId: string,
  versionAId: string,
  versionBId: string,
): Promise<PropostaVersionDiff> {
  const { data } = await api.get<PropostaVersionDiff>(
    `/proposals/${proposalId}/versions/${versionAId}/diff/${versionBId}`,
  );
  return data;
}

// ---- Cadastro / edição ----------------------------------------------

export async function createProposal(payload: CreateProposalPayload): Promise<PropostaComVersoes> {
  const { data } = await api.post<PropostaComVersoes>('/proposals', payload);
  return data;
}

export async function createFromOpportunity(
  opportunityId: string,
  payload: CreateProposalFromOpportunityPayload,
): Promise<PropostaComVersoes> {
  const { data } = await api.post<PropostaComVersoes>(
    `/proposals/nova-de-oportunidade/${opportunityId}`,
    payload,
  );
  return data;
}

export async function updateProposal(
  id: string,
  payload: UpdateProposalPayload,
): Promise<PropostaComVersoes> {
  const { data } = await api.patch<PropostaComVersoes>(`/proposals/${id}`, payload);
  return data;
}

export async function deleteProposal(id: string): Promise<void> {
  await api.delete(`/proposals/${id}`);
}

// ---- Itens ----------------------------------------------------------

export async function addItem(
  proposalId: string,
  versionId: string,
  payload: AddProposalItemPayload,
): Promise<PropostaItem> {
  const { data } = await api.post<PropostaItem>(
    `/proposals/${proposalId}/versions/${versionId}/items`,
    payload,
  );
  return data;
}

export async function updateItem(
  proposalId: string,
  itemId: string,
  payload: UpdateProposalItemPayload,
): Promise<PropostaItem> {
  const { data } = await api.patch<PropostaItem>(
    `/proposals/${proposalId}/items/${itemId}`,
    payload,
  );
  return data;
}

export async function removeItem(
  proposalId: string,
  itemId: string,
): Promise<void> {
  await api.delete(`/proposals/${proposalId}/items/${itemId}`);
}

export async function seedItemsFromCommercialBase(
  proposalId: string,
  versionId: string,
): Promise<PropostaItem[]> {
  const { data } = await api.post<PropostaItem[] | { data: PropostaItem[] }>(
    `/proposals/${proposalId}/versions/${versionId}/items/from-commercial-base`,
  );
  return unwrapList<PropostaItem>(data);
}

// ---- Anexos ---------------------------------------------------------

export async function uploadAttachment(
  proposalId: string,
  versionId: string,
  file: File,
  descricao?: string,
): Promise<PropostaAnexo> {
  const form = new FormData();
  form.append('file', file);
  if (descricao) form.append('descricao', descricao);
  const { data } = await api.post<PropostaAnexo>(
    `/proposals/${proposalId}/versions/${versionId}/attachments`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}

export async function removeAttachment(
  proposalId: string,
  versionId: string,
  attachmentId: string,
): Promise<void> {
  await api.delete(
    `/proposals/${proposalId}/versions/${versionId}/attachments/${attachmentId}`,
  );
}

export function urlDownloadAnexo(proposalId: string, attachmentId: string): string {
  return `/proposals/${proposalId}/attachments/${attachmentId}/download`;
}

// ---- Destinatários --------------------------------------------------

export async function addRecipient(
  proposalId: string,
  versionId: string,
  payload: { contactId: string; isPrimary?: boolean },
): Promise<{ id: string }> {
  const { data } = await api.post<{ id: string }>(
    `/proposals/${proposalId}/versions/${versionId}/recipients`,
    payload,
  );
  return data;
}

export async function removeRecipient(
  proposalId: string,
  versionId: string,
  recipientId: string,
): Promise<void> {
  await api.delete(
    `/proposals/${proposalId}/versions/${versionId}/recipients/${recipientId}`,
  );
}

// ---- Ciclo de vida (transições) -------------------------------------

export async function requestApproval(
  proposalId: string,
  versionId: string,
  workflowId?: string,
): Promise<unknown> {
  const { data } = await api.post(
    `/proposals/${proposalId}/versions/${versionId}/request-approval`,
    workflowId ? { workflowId } : {},
  );
  return data;
}

export async function decideApproval(
  approvalId: string,
  payload: DecideApprovalPayload,
): Promise<PropostaAprovacao> {
  const { data } = await api.post<PropostaAprovacao>(
    `/proposal-approvals/${approvalId}/decide`,
    payload,
  );
  return data;
}

/**
 * Envia versão da proposta ao cliente. O PDF é gerado no cliente
 * (Open Question #4) e enviado em multipart. `dispatchInput` viaja em
 * `data` no form.
 */
export async function dispatchProposal(
  proposalId: string,
  versionId: string,
  file: File,
  input: DispatchProposalPayload,
): Promise<PropostaDispatch> {
  const form = new FormData();
  form.append('file', file);
  form.append('recipients', JSON.stringify(input.recipients));
  form.append('medium', input.medium);
  if (input.notes) form.append('notes', input.notes);
  const { data } = await api.post<PropostaDispatch>(
    `/proposals/${proposalId}/versions/${versionId}/dispatch`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}

export async function recordNegotiation(
  proposalId: string,
  versionId: string,
  payload: RecordNegotiationPayload,
): Promise<{ event: PropostaEventoNegociacao; generatedVersion?: PropostaVersao }> {
  const { data } = await api.post<{
    event: PropostaEventoNegociacao;
    generatedVersion?: PropostaVersao;
  }>(`/proposals/${proposalId}/versions/${versionId}/negotiation-events`, payload);
  return data;
}

export async function recordAcceptance(
  proposalId: string,
  versionId: string,
  payload: RecordAcceptancePayload,
): Promise<PropostaAceite> {
  const { data } = await api.post<PropostaAceite>(
    `/proposals/${proposalId}/versions/${versionId}/acceptance`,
    payload,
  );
  return data;
}

export async function recordRejection(
  proposalId: string,
  versionId: string,
  payload: RecordRejectionPayload,
): Promise<PropostaRejeicao> {
  const { data } = await api.post<PropostaRejeicao>(
    `/proposals/${proposalId}/versions/${versionId}/rejection`,
    payload,
  );
  return data;
}

export async function convertToContract(
  proposalId: string,
  versionId: string,
  payload: ConvertToContractPayload,
): Promise<{ proposal: Proposta; version: PropostaVersao }> {
  const { data } = await api.post<{ proposal: Proposta; version: PropostaVersao }>(
    `/proposals/${proposalId}/versions/${versionId}/convert-to-contract`,
    payload,
  );
  return data;
}

// ---- Duplicação (RN-COM-025) ----------------------------------------

export async function duplicateProposal(id: string): Promise<PropostaComVersoes> {
  const { data } = await api.post<PropostaComVersoes>(`/proposals/${id}/duplicate`);
  return data;
}

// ---- Assinatura eletrônica (feature dormente até adapter concreto) --

export async function requestSignature(
  proposalId: string,
  versionId: string,
  payload: RequestSignaturePayload,
): Promise<PropostaAssinatura> {
  const { data } = await api.post<PropostaAssinatura>(
    `/proposals/${proposalId}/versions/${versionId}/signatures`,
    payload,
  );
  return data;
}

// ---- Admin de workflows (mini-fatia 2) ------------------------------

export type TipoWorkflowProposta =
  | 'default'
  | 'novo_negocio'
  | 'renovacao'
  | 'upsell'
  | 'cross_sell'
  | 'licitacao';

export type ModoExecucaoWorkflow = 'sequencial' | 'paralelo';

export type TipoCondicaoStepAprovacao =
  | 'always'
  | 'discount_above_limit'
  | 'margin_below_min'
  | 'value_above';

export interface ApprovalWorkflow {
  id: string;
  nome: string;
  tipo: TipoWorkflowProposta;
  modoExecucao: ModoExecucaoWorkflow;
  ativo: boolean;
  descricao?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ApprovalWorkflowStep {
  id: string;
  workflowId: string;
  ordem: number;
  roleSlug: string;
  featureRequired: string;
  conditionType: TipoCondicaoStepAprovacao;
  conditionValue?: string;
  ativo: boolean;
  createdAt: string;
}

export interface ApprovalThreshold {
  id: string;
  chave: string;
  valor: string;
  descricao?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateWorkflowPayload {
  nome: string;
  tipo: TipoWorkflowProposta;
  modoExecucao?: ModoExecucaoWorkflow;
  ativo?: boolean;
  descricao?: string;
}

export type UpdateWorkflowPayload = Partial<CreateWorkflowPayload>;

export interface CreateWorkflowStepPayload {
  ordem: number;
  roleSlug: string;
  featureRequired: string;
  conditionType: TipoCondicaoStepAprovacao;
  conditionValue?: number;
  ativo?: boolean;
}

export type UpdateWorkflowStepPayload = Partial<CreateWorkflowStepPayload>;

export interface UpsertThresholdPayload {
  valor: number;
  descricao?: string;
}

export const ADMIN_WORKFLOW_KEYS = {
  all: ['propostas', 'admin-workflows'] as const,
  list: () => ['propostas', 'admin-workflows', 'list'] as const,
  detail: (id: string) => ['propostas', 'admin-workflows', 'detail', id] as const,
  thresholds: () => ['propostas', 'admin-workflows', 'thresholds'] as const,
};

export async function listWorkflows(): Promise<ApprovalWorkflow[]> {
  const { data } = await api.get<ApprovalWorkflow[] | { data: ApprovalWorkflow[] }>(
    `/admin/proposal-workflows`,
  );
  return unwrapList<ApprovalWorkflow>(data);
}

export async function getWorkflow(
  id: string,
): Promise<{ workflow: ApprovalWorkflow; steps: ApprovalWorkflowStep[] }> {
  const { data } = await api.get<{ workflow: ApprovalWorkflow; steps: ApprovalWorkflowStep[] }>(
    `/admin/proposal-workflows/${id}`,
  );
  return data;
}

export async function createWorkflow(payload: CreateWorkflowPayload): Promise<ApprovalWorkflow> {
  const { data } = await api.post<ApprovalWorkflow>('/admin/proposal-workflows', payload);
  return data;
}

export async function updateWorkflow(
  id: string,
  payload: UpdateWorkflowPayload,
): Promise<ApprovalWorkflow> {
  const { data } = await api.patch<ApprovalWorkflow>(`/admin/proposal-workflows/${id}`, payload);
  return data;
}

export async function deleteWorkflow(id: string): Promise<void> {
  await api.delete(`/admin/proposal-workflows/${id}`);
}

export async function addWorkflowStep(
  workflowId: string,
  payload: CreateWorkflowStepPayload,
): Promise<ApprovalWorkflowStep> {
  const { data } = await api.post<ApprovalWorkflowStep>(
    `/admin/proposal-workflows/${workflowId}/steps`,
    payload,
  );
  return data;
}

export async function updateWorkflowStep(
  workflowId: string,
  stepId: string,
  payload: UpdateWorkflowStepPayload,
): Promise<ApprovalWorkflowStep> {
  const { data } = await api.patch<ApprovalWorkflowStep>(
    `/admin/proposal-workflows/${workflowId}/steps/${stepId}`,
    payload,
  );
  return data;
}

export async function removeWorkflowStep(workflowId: string, stepId: string): Promise<void> {
  await api.delete(`/admin/proposal-workflows/${workflowId}/steps/${stepId}`);
}

export async function listThresholds(): Promise<ApprovalThreshold[]> {
  const { data } = await api.get<ApprovalThreshold[] | { data: ApprovalThreshold[] }>(
    `/admin/proposal-workflows/thresholds`,
  );
  return unwrapList<ApprovalThreshold>(data);
}

export async function upsertThreshold(
  chave: string,
  payload: UpsertThresholdPayload,
): Promise<ApprovalThreshold> {
  const { data } = await api.put<ApprovalThreshold>(
    `/admin/proposal-workflows/thresholds/${chave}`,
    payload,
  );
  return data;
}
